// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

// ** Components
import Section from '@/components/common/Section';
import FileCarousel from './FileCarousel';
import FileCardsGrid from './FileCardsGrid';
import ComponentBlocksSearchFilter from '@/components/strapi/Shared/ComponentBlocksSearchFilter';

// ** Types
import type { DefaultSession } from 'next-auth';
import type { StrapiComponentProps } from '@/types/strapi';
import type { FileCardItem as CommonFileCardItem } from '@/components/common/FileCard';
import type { UploadFile, ComponentBlocksFileBlock as BlocksFileBlockProps } from '@/types/graphql';
import { getStrapiFileUrl } from '@/utils/api/strapi';

type AuthentikUser = DefaultSession['user'] & {
	role?: string;
	groups?: string[];
	cobraId?: string;
	dbStatus?: string;
};

type ComponentBlocksFileBlockProps = StrapiComponentProps<BlocksFileBlockProps> & {
	currentUser?: AuthentikUser;
	groupMap?: Record<string, string>;
};

type FileCardItem = CommonFileCardItem & {
	category: string;
	year: string;
	isAuthenticated: boolean;
	isLocked: boolean;
	lockStatus: 'public' | 'allowed' | 'unauthenticated' | 'unauthorized';
	actionLabel: string;
	accessRestriction?: string | null | undefined;
	rawFile: UploadFile;
	requiredPks: string[];
};

const ComponentBlocksFileBlock = ({
	host,
	containerSx,
	fileBlockContent,
	fileBlockSectionTitle,
	fileBlockBackground,
	fileBlockAuthenticated,
	fileBlockAccessRestriction,
	fileBlockLayout,
	fileBlockSearchFilter,
	currentUser,
	groupMap = {}
}: ComponentBlocksFileBlockProps) => {
	const FALLBACK_CATEGORY = 'Ohne Kategorie';
	const FALLBACK_PERIOD = 'Ohne Zeitraum';

	const [fetchedGroupPks, setFetchedGroupPks] = React.useState<string[]>([]);
	const [isPermissionsLoading, setIsPermissionsLoading] = React.useState<boolean>(!!currentUser);

	React.useEffect(() => {
		if (!currentUser) {
			setIsPermissionsLoading(false);
			return;
		}

		const fetchUserBackendGroups = async () => {
			try {
				const response = await fetch('/api/current-user-groups');
				if (response.ok) {
					const data = await response.json();
					const pks = Array.isArray(data?.groups) ? data.groups : [];
					setFetchedGroupPks(pks);
				}
			} catch (error) {
				console.error('Failed fetching user group relations from proxy gateway:', error);
			} finally {
				setIsPermissionsLoading(false);
			}
		};

		fetchUserBackendGroups();
	}, [currentUser]);

	const normalizedItems = React.useMemo<FileCardItem[]>(() => {
		if (!fileBlockContent) return [];

		const isRestrictionEmpty = (r: any) => !r || r === '[]' || r === '""' || r.trim() === '' || r === null;

		return fileBlockContent
			.map((item, index) => {
				if (!item?.dataFile?.file) return null;

				const file = item.dataFile.file;
				const fileName = item.dataFile.fileName || file.name;
				const itemRestriction = item.dataFile.fileAccessRestrictions;

				// 1. Resolve Authentication Priority
				const resolvedAuthenticated = !!fileBlockAuthenticated || !!item.dataFile.fileAuthenticated;

				// 2. Strict Hierarchy: Parent (if active) overrides Item
				let resolvedRestrictions: string | string[] | null = null;
				const rawBlockRes = fileBlockAccessRestriction as string | string[] | null;
				const rawItemRes = itemRestriction as string | string[] | null;
				if (fileBlockAuthenticated && !isRestrictionEmpty(rawBlockRes)) {
					resolvedRestrictions = rawBlockRes;
				} else if (!isRestrictionEmpty(rawItemRes)) {
					resolvedRestrictions = rawItemRes;
				}

				// 3. Parse Requirements
				let cleanRequiredPks: string[] = [];
				if (resolvedRestrictions) {
					if (typeof resolvedRestrictions === 'string') {
						try {
							const parsed = JSON.parse(resolvedRestrictions);
							cleanRequiredPks = Array.isArray(parsed) ? parsed.map(String) : [String(resolvedRestrictions)];
						} catch {
							cleanRequiredPks = [resolvedRestrictions.trim()];
						}
					} else if (Array.isArray(resolvedRestrictions)) {
						cleanRequiredPks = resolvedRestrictions.map(String);
					}
				}
				cleanRequiredPks = cleanRequiredPks.map((pk) => pk.replace(/^\s+|\s+$/g, '')).filter(Boolean);

				// 4. Resolve Access State
				let userHasGroupAccess = true;
				if (resolvedAuthenticated && cleanRequiredPks.length > 0) {
					if (!currentUser) {
						userHasGroupAccess = false;
					} else {
						const target = cleanRequiredPks.map((pk) => pk.toLowerCase().trim());
						const userGroups = (currentUser.groups || []).map((g) => g.toLowerCase().trim());
						const fetched = fetchedGroupPks.map((pk) => pk.toLowerCase().trim());

						const hasIdMatch = fetched.some((pk) => target.includes(pk));
						const hasNameMatch = target.some((name) => userGroups.includes(name));
						const hasMapMatch = target.some(
							(pk) => groupMap[pk] && userGroups.includes(groupMap[pk].toLowerCase().trim())
						);

						userHasGroupAccess = hasIdMatch || hasNameMatch || hasMapMatch;
					}
				}

				// 5. Determine Final Status
				let lockStatus: FileCardItem['lockStatus'] = 'public';
				if (resolvedAuthenticated) {
					if (!currentUser) lockStatus = 'unauthenticated';
					else if (!userHasGroupAccess) lockStatus = 'unauthorized';
					else lockStatus = 'allowed';
				}

				const isLocked = lockStatus === 'unauthenticated' || lockStatus === 'unauthorized';
				const actionLabel =
					lockStatus === 'unauthenticated'
						? 'Zum Download anmelden'
						: lockStatus === 'unauthorized'
							? 'Fehlende Berechtigung'
							: 'Download';

				// --- URL PROCESSING HANDLER ---
				const fullUrl = file?.url ? getStrapiFileUrl({ host, url: file.url }) : '';

				// Shallow copy and append absolute path parameters down to rawFile
				const updatedRawFile = file ? { ...file, url: fullUrl } : file;

				return {
					documentId: item.id || item.__typename || `file-${index}`,
					fileName: fileName || '',
					url: fullUrl,
					description: item.dataFile.fileDescription || '',
					category: item.dataFile.fileCategory?.trim() || 'Ohne Kategorie',
					date: (item.dataFile.fileDate as string) || '',
					year: item.dataFile.fileDate
						? String(new Date(item.dataFile.fileDate as string).getFullYear())
						: 'Ohne Zeitraum',
					rawFile: updatedRawFile,
					isAuthenticated: resolvedAuthenticated,
					isLocked: isLocked,
					lockStatus: lockStatus,
					actionLabel: actionLabel,
					requiredPks: cleanRequiredPks,
					accessRestriction: resolvedRestrictions as string | null
				} as FileCardItem;
			})
			.filter((item): item is FileCardItem => item !== null);
	}, [
		fileBlockContent,
		fileBlockAuthenticated,
		fileBlockAccessRestriction,
		currentUser,
		groupMap,
		fetchedGroupPks,
		host
	]);

	const [selectedCategory, setSelectedCategory] = React.useState<string>('');
	const [selectedPeriod, setSelectedPeriod] = React.useState<string>('');
	const [searchTerm, setSearchTerm] = React.useState<string>('');

	const hasSearchFilterConfiguration = !!fileBlockSearchFilter;

	React.useEffect(() => {
		if (!hasSearchFilterConfiguration) {
			setSelectedCategory('');
			setSelectedPeriod('');
			setSearchTerm('');
		}
	}, [hasSearchFilterConfiguration]);

	const filterOptions = React.useMemo(() => {
		const categoriesSet = new Set<string>();
		const periodsSet = new Set<string>();

		normalizedItems.forEach((item) => {
			if (item.category) categoriesSet.add(item.category);
			if (item.year) periodsSet.add(item.year);
		});

		const sortedCategories = Array.from(categoriesSet).sort((a, b) => {
			if (a === FALLBACK_CATEGORY) return 1;
			if (b === FALLBACK_CATEGORY) return -1;
			return a.localeCompare(b);
		});

		const sortedPeriods = Array.from(periodsSet).sort((a, b) => {
			if (a === FALLBACK_PERIOD) return 1;
			if (b === FALLBACK_PERIOD) return -1;
			return b.localeCompare(a);
		});

		return {
			categories: [
				{
					value: '',
					label: fileBlockSearchFilter?.searchFilterCategoryPlaceholder || 'Alle Kategorien'
				},
				...sortedCategories.map((cat) => ({ value: cat, label: cat }))
			],
			periods: [
				{
					value: '',
					label: fileBlockSearchFilter?.searchFilterPeriodPlaceholder || 'Alle Zeiträume'
				},
				...sortedPeriods.map((yr) => ({ value: yr, label: yr }))
			]
		};
	}, [normalizedItems, fileBlockSearchFilter]);

	const visibleFiles = React.useMemo(() => {
		const normalizedTerm = searchTerm.trim().toLowerCase();
		return normalizedItems.filter((file) => {
			const matchesCategory = !selectedCategory || selectedCategory === 'all' || file.category === selectedCategory;

			const matchesYear = !selectedPeriod || file.year === selectedPeriod;

			const matchesSearch =
				!normalizedTerm ||
				file.fileName.toLowerCase().includes(normalizedTerm) ||
				file.description?.toLowerCase().includes(normalizedTerm);

			return matchesCategory && matchesYear && matchesSearch;
		});
	}, [normalizedItems, searchTerm, selectedCategory, selectedPeriod]);

	if (isPermissionsLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
				<CircularProgress size={40} />
			</Box>
		);
	}

	return (
		<Section containerSx={containerSx} backgroundColor={fileBlockBackground ? 'heroLightBackground.main' : undefined}>
			{fileBlockSectionTitle && (
				<Typography
					variant="title-lg"
					component="h2"
					sx={{
						mb: 10,
						color: 'common.black',
						fontSize: { xs: '2rem', md: '2.5rem' },
						lineHeight: { xs: '2.5rem', md: '3rem' },
						letterSpacing: { xs: '-0.64px', md: '-1.2px' }
					}}
				>
					{fileBlockSectionTitle}
				</Typography>
			)}

			{hasSearchFilterConfiguration && (
				<Box sx={{ width: '100%', mb: 6 }}>
					<ComponentBlocksSearchFilter
						config={{
							searchFilterShowCategory: !!fileBlockSearchFilter.searchFilterShowCategory,
							searchFilterShowPeriod: !!fileBlockSearchFilter.searchFilterShowPeriod,
							searchFilterShowSearch: !!fileBlockSearchFilter.searchFilterShowSearch,
							categoryPlaceholder: fileBlockSearchFilter.searchFilterCategoryPlaceholder || 'Kategorie',
							periodPlaceholder: fileBlockSearchFilter.searchFilterPeriodPlaceholder || 'Zeitraum',
							searchPlaceholder: fileBlockSearchFilter.searchFilterSearchPlaceholder || 'Suchen...'
						}}
						options={filterOptions}
						state={{ selectedCategory, selectedPeriod, searchTerm }}
						handlers={{
							onCategoryChange: (val) => setSelectedCategory(val ?? ''),
							onPeriodChange: (val) => setSelectedPeriod(val ?? ''),
							onSearchChange: setSearchTerm,
							onSearchSubmit: setSearchTerm
						}}
						backgroundColor="#FAFAFA"
					/>
				</Box>
			)}

			{fileBlockLayout === 'Grid' && (
				<Box
					sx={{
						px: fileBlockBackground ? { xs: 4, md: 6 } : undefined,
						py: fileBlockBackground ? 10 : 0
					}}
				>
					<FileCardsGrid showAvailabilityCount={false} sx={{ mb: 4 }} items={visibleFiles} currentUser={currentUser} />
				</Box>
			)}

			{fileBlockLayout === 'Carousel' && <FileCarousel items={visibleFiles} currentUser={currentUser} />}
		</Section>
	);
};

export default ComponentBlocksFileBlock;
